/* eslint-disable import/no-cycle */
import React from 'react';
import classnames from 'classnames';
import get from 'lodash/get';
import { arrayToObject } from 'utils/transformations';
import { PAYMENT_GROUPS, PAYMENT_TYPES, WORKFLOWS } from 'constants.js';

import WirePayment from '../wirePayment/WirePayment';

/**
 *
 * @param {object} context react context, we are going to extend
 * @param {object} options datasource attached to context
 * @returns {void}
 */
export const applyToContext = (context, options) => {
  Object.keys(options).forEach((opt) => {
    // eslint-disable-next-line no-param-reassign
    context[opt] = options[opt];
  });
};

/**
 * Get appropriate message based on error code.
 *
 * @param {object} locale object with all the locales, used across the app
 * @param {*} code code we need to match against
 * @returns {string} returns matched code message or default
 */
export const getErrorMessage = (locale, code, msg) => {
  const getLocale = (str) => locale[`create.payment.validation.${str}`];

  const messages = {
    'PAYMENTS.VALIDATION_SUCCESS': getLocale('success'),
    'PAYMENTS.PAYMENT_NOT_FOUND': getLocale('404'),
    'PAYMENTS.VALIDATION.TEMPLATE_NAME': getLocale('templateName'),
    'PAYMENTS.VALIDATION.TEMPLATE_CODE': getLocale('templateCode'),
    'PAYMENTS.TEMPLATE_ALREADY_EXISTS': getLocale('templateExists'),
    'PAYMENTS.VALIDATION_PAYMENT_METHOD': getLocale('paymentMethod'),
    'PAYMENTS.VALIDATION.BANK_TRACE_NUM': getLocale('bankTrace'),
    'PAYMENTS.REQUIRED_PAYMENT_TYPE': getLocale('paymentType'),
    'PAYMENTS.REQUIRED_ORIGINATOR_ACCOUNT_ID': getLocale('required'),
    'PAYMENTS.REQUIRED_BENEFICIARY_ACCOUNT_INFO': getLocale('required'),
    'PAYMENTS.REQUIRED_ADDRESS1': getLocale('required'),
    'PAYMENTS.REQUIRED_ADDRESS2': getLocale('required'),
    'PAYMENTS.REQUIRED_ADDRESS3': getLocale('required'),
    'PAYMENTS.REQUIRED_BENEFICIARY_BANK_INFO': getLocale('required'),
    'PAYMENTS.REQUIRED_BENEFICIARY_BANK_CODE': getLocale('required'),
    'PAYMENTS.REQUIRED_BEBEFICIARY_ACCOUNT_NUMBER': getLocale('required'),
    'PAYMENTS.REQUIRED_BEBEFICIARY_ACCOUNT_NAME': getLocale('required'),
    'PAYMENTS.VALIDATION.AMOUNT': getLocale('amount'),
    'PAYMENTS.INVALID_TRANSACTION_DATE': getLocale('transactionDate'),
    'PAYMENTS.EXCEEDS_TRANSACTION_LIMIT': getLocale('transactionLimit'),
    'PAYMENTS.EXCEEDS_BANK_TEMPLATE_LIMIT': getLocale('bankTemplateLimit'),
    'PAYMENTS.EXCEEDS_DAILY_TRANSACTION_LIMIT': getLocale('dailyTransactionLimit'),
    'PAYMENTS.EXCEEDS_DAILY_BANK_TEMPLATE_LIMIT': getLocale('dailyBankTemplateLimit'),
    'PAYMENTS.EXCEEDS_TRANSACTION_APPROVAL_LIMIT': getLocale('transactionApprovalLimit'),
    'PAYMENTS.EXCEEDS_BANK_TEMPLATE_APPROVAL_LIMIT': getLocale('bankTemplateApprovalLimit'),
    'PAYMENTS.EXCEEDS_DAILY_TRANSACTION_APPROVAL_LIMIT': getLocale(
      'dailyTransactionApprovalLimit'
    ),
    'PAYMENTS.EXCEEDS_DAILY_BANK_TEMPLATE_APPROVAL_LIMIT': getLocale(
      'dailyBankTemplateApprovalLimit'
    ),
    'PAYMENTS.EXCHANGE_RATE_NOT_ALLOWED_TO_MODIFY': getLocale('rateNotAllowedToModify'),
    'PAYMENTS.CHARGES_NOT_ALLOWED_TO_DISPLAY': getLocale('chargesNotAllowedToDisplay'),
    'PAYMENTS.INTERMEDIARY_BANK_NOT_ALLOWED': getLocale('intermediaryBankNotAllowed'),
    'PAYMENTS.INVALID_CHARACTER_NOT_ALLOWED': getLocale('invalidCharacterNotAllowed'),
    'PAYMENTS.VALIDATION_PROVIDE_RATE_CONFIRMATION': getLocale('provideRateConfirmation'),
    'PAYMENTS.CONFIG_USER_LIMITS': getLocale('submitUserLimits'),
    'PAYMENTS.CONF_MISSING_APPROVAL_REQ': getLocale('missingApprovalReq'),
    'COMMON.USER_ACTION_NOT_PERMITTED_IN_EMULATION': 'EMULATION_ERROR'
  };

  return messages[code] || locale[code] || msg || locale['create.payment.error'];
};

/**
 * Get proper api error, based on response.
 *
 * @param {object} locale
 * @param {object} data
 * @param {object} result
 * @returns {object}
 */
export const getApiError = (locale, data, result) => {
  const code = get(data, '[0].code', null);
  const message = get(data, '[0].userMessage', null);
  const isCorba = code && code.includes('CORBA_ERROR');
  const errorGuid = get(result, 'guid');
  return {
    error: {
      thrown: !!code,
      message: (code && (isCorba ? message : getErrorMessage(locale, code))) || '',
      errorGuid
    }
  };
};

/**
 * Builds a props object for a FlexDropDown component.
 *
 * @param {object} locale
 * @param {string} type
 * @param {bool} gutters
 * @param {bool} formGroup
 * @param {bool} stretch
 * @param {object} extra
 * @returns {object}
 */
export const buildDropdownProps = (
  locale,
  { field, formGroup = true, gutters = true, noClass, rootClassName, type, ...extra }
) => ({
  // filterFunction: (options, filterString) => {
  //   const query = new RegExp(`${filterString}`, 'i');
  //   return options.filter((o) => o.name.match(query));
  // },
  className: !noClass
    ? classnames('flex-dropdown', rootClassName, {
        'form-group': formGroup,
        'fixed-width': extra.width,
        gutters
      })
    : '',
  name: field,
  id: field,
  dataQa: `${type}-${field}`,
  placeholder: locale['common.select'],
  ...extra
});

/**
 * Get appropriate group flags
 * @param {string} type (Domestic | International)
 * @returns {object}
 */
export const getGroupFlags = (type) => {
  if (!type) {
    return null;
  }

  return arrayToObject(
    PAYMENT_GROUPS.map((group) => {
      const groupValues = Object.values(PAYMENT_TYPES[group]).map((x) => x.toLowerCase());

      return {
        [`is${group.charAt()}${group.slice(1).toLowerCase()}`]: groupValues.includes(
          type.toLowerCase()
        )
      };
    })
  );
};

/**
 * Get payment type out of from state object
 * @param {object} formState
 */
export const getPaymentType = (formState, options, { copy, property, template = false } = {}) => {
  const name =
    formState.paymentType &&
    get(formState.paymentType, `0.${template ? 'type' : 'name'}`, get(formState, 'paymentType'));
  const match =
    template && !copy
      ? options.find((c) => c[template ? 'paymentType' : 'id'] === name)
      : [].concat(...options.map((x) => x.child)).find((c) => c.name.includes(name));

  return match && match[template ? property || 'paymentType' : 'type'];
};

/**
 * Retrieve proper view, based on `paymentType`.
 *
 * @param {object} fields
 * @param {object} props
 * @returns {HTMLElement}
 */
export const buildAppropriateViewComponent = (
  fields,
  { history, isTemplate, locale, paymentTypeOptions, ...props }
) => {
  const type = get(
    fields,
    'paymentType[0].type',
    getPaymentType(fields, paymentTypeOptions, { template: isTemplate })
  );

  const { isWire } = getGroupFlags(type);
  if (isWire) {
    const wireProps = {
      fields: {
        ...fields,
        paymentTypeStr: type
      },
      paymentTypeOptions,
      isTemplate,
      ...props
    };

    return <WirePayment {...wireProps} />;
  }

  // If ACH type got to ACH workflow
  if (
    type.includes('IAT') ||
    type.includes('PPD') ||
    type.includes('CCD') ||
    type.includes('WEB') ||
    type.includes('TELDO') ||
    type.includes('RCKDO') ||
    type.includes('TXP')
  ) {
    return history.push({
      pathname: `${WORKFLOWS.CREATE_ACH_PAYMENT}/${type}`,
      state: {
        workflowStartPath: get(history, 'location.state.workflowStartPath'),
        // Add more specific title for tax payments
        ...(type.includes('TXP') ? { actionTitle: 'Initiate Tax Payment' } : [])
      }
    });
  }
  // TODO: Not sure when this would run
  return history.push({
    pathname: `${WORKFLOWS.CREATE_ACH_PAYMENT}/${type}`,
    state: {
      workflowStartPath: get(history, 'location.state.workflowStartPath')
    }
  });
};
